// Task ("поручение") helpers. A task is an E2E message (head.oztask=1) whose
// encrypted content is a task object. Status changes are separate E2E update
// messages (head.oztaskUpdate=<taskId>) that clients FOLD into the current state
// (assignees can't edit the creator's message in Tinode, so updates are append-only).

export const OZTASK = 'oztask';        // head marker on a task message
export const OZTASK_UPDATE = 'oztaskUpdate'; // head marker (=taskId) on an update
export const OZTASK_REPORT = 'oztaskReport'; // head marker (=taskId) on a report (E2E file/text)

// Build a new task object (the encrypted message content).
export function makeTask({ title, desc, assignees, deadline, prio, creator }) {
  return {
    v: 1,
    id: 'tsk-' + Math.random().toString(36).slice(2, 11) + Date.now().toString(36),
    title: (title || '').trim(),
    desc: (desc || '').trim(),
    assignees: Array.isArray(assignees) ? assignees.slice() : [],
    deadline: deadline || null, // epoch ms or null
    prio: prio || 'mid',        // 'low' | 'mid' | 'high'
    creator: creator || null,
  };
}

// Build an update object (the encrypted content of an update message).
// `status` may be omitted (falsy) for a comment-only update — folding ignores
// updates without a status, so a bare note never changes the task's state.
// `note` is an optional free-text comment (assignee's text report / creator's
// return reason).
export function makeUpdate(taskId, status, by, note) {
  const u = { v: 1, taskId, by };
  if (status) { u.status = status; } // status: progress|review|done|new
  const n = (note == null ? '' : String(note)).trim();
  if (n) { u.note = n; }
  return u;
}

// Fold a task + its update messages (chronological) into the current status.
// updates: [{status, by, ts}]. Returns 'new'|'progress'|'review'|'done'.
export function foldStatus(task, updates) {
  let status = 'new';
  if (Array.isArray(updates)) {
    for (const u of updates) {
      if (u && u.status) {
        status = u.status;
      }
    }
  }
  return status;
}

// Effective status incl. the derived "overdue" (deadline passed & not done).
export function effectiveStatus(status, deadline, now) {
  if (status !== 'done' && deadline && deadline < (now || Date.now())) {
    return 'overdue';
  }
  return status;
}

const STATUS_META = {
  new:      { cls: 'st-new',    key: 'task_status_new' },
  progress: { cls: 'st-prog',   key: 'task_status_progress' },
  review:   { cls: 'st-review', key: 'task_status_review' },
  done:     { cls: 'st-done',   key: 'task_status_done' },
  overdue:  { cls: 'st-over',   key: 'task_status_overdue' },
};
export function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.new;
}

export function isAssignee(task, uid) {
  return !!(task && Array.isArray(task.assignees) && uid && task.assignees.indexOf(uid) >= 0);
}
export function isCreator(task, uid) {
  return !!(task && task.creator && task.creator === uid);
}

// Count NEW task signals for the nav-rail badge: task / task-update / task-report
// messages authored by SOMEONE ELSE and newer than `seenTs` (when the user last
// opened the Tasks section). Uses only the UNENCRYPTED message heads, so it works
// even for chats whose content hasn't been decrypted this session. `seenTs` is epoch
// ms (0 = never opened). Returns an integer count.
export function countNewTaskSignals(tinode, seenTs, myUid) {
  let count = 0;
  let me = null;
  try { me = tinode.getMeTopic(); } catch (e) { return 0; }
  if (!me || !me.contacts) { return 0; }
  const since = seenTs || 0;
  me.contacts((c) => {
    const name = c && c.topic;
    if (!name) { return; }
    let topic = null;
    try { topic = tinode.getTopic(name); } catch (e) { return; }
    if (!topic || !topic.messages) { return; }
    topic.messages((m) => {
      const h = m && m.head;
      if (!h || !(h[OZTASK] || h[OZTASK_UPDATE] || h[OZTASK_REPORT])) { return; }
      if (m.from && m.from === myUid) { return; } // my own actions aren't "new" to me
      const ts = m.ts ? new Date(m.ts).getTime() : 0;
      if (ts > since) { count++; }
    });
  });
  return count;
}

// Aggregate tasks across ALL chats for the "Задачи" section. Reads only the
// ALREADY-DECRYPTED task content from the plaintext cache (getPlain) — so it
// never advances a ratchet and stays E2E-safe; coverage = chats decrypted this
// session. Returns [{task, status, topicName, isGroup, updates, reports}], newest
// deadline first (undated last). `getPlain(topicName, seq)` and `now` (epoch ms)
// are injected so this stays free of app singletons.
export function aggregateTasks(tinode, getPlain, now) {
  const byId = {}; // taskId -> {task, topicName, isGroup, updates:[], reports:[]}
  let me = null;
  try { me = tinode.getMeTopic(); } catch (e) { /* ignore */ }
  if (!me || !me.contacts) { return []; }

  me.contacts((c) => {
    const name = c && c.topic;
    if (!name) { return; }
    let topic = null;
    try { topic = tinode.getTopic(name); } catch (e) { /* ignore */ }
    if (!topic || !topic.messages) { return; }
    const isGroup = !!(topic.isGroupType && topic.isGroupType());
    topic.messages((m) => {
      const h = m.head;
      if (!h) { return; }
      const ensure = (tid) => (byId[tid] = byId[tid] || { updates: [], reports: [] });
      if (h[OZTASK]) {
        const content = getPlain(name, m.seq);
        if (content && typeof content === 'object' && content.title != null && content.id) {
          const e = ensure(content.id);
          e.task = content; e.topicName = name; e.isGroup = isGroup; e.ts = m.ts;
        }
      } else if (h[OZTASK_UPDATE]) {
        const content = getPlain(name, m.seq);
        if (content && (content.status || content.note)) { ensure(h[OZTASK_UPDATE]).updates.push({ status: content.status, by: content.by, ts: m.ts, note: content.note }); }
      } else if (h[OZTASK_REPORT]) {
        ensure(h[OZTASK_REPORT]).reports.push({ seq: m.seq, ts: m.ts, by: m.from });
      }
    });
  });

  const out = [];
  const t = now || Date.now();
  for (const tid in byId) {
    const e = byId[tid];
    if (!e.task) { continue; } // orphan updates whose task isn't decrypted/loaded
    let st = foldStatus(e.task, e.updates);
    st = effectiveStatus(st, e.task.deadline, t);
    out.push({ task: e.task, status: st, topicName: e.topicName, isGroup: e.isGroup, updates: e.updates, reports: e.reports, ts: e.ts });
  }
  // Sort: overdue first, then by soonest deadline, undated last.
  out.sort((a, b) => {
    const ao = a.status === 'overdue' ? 0 : 1, bo = b.status === 'overdue' ? 0 : 1;
    if (ao !== bo) { return ao - bo; }
    const ad = a.task.deadline || Infinity, bd = b.task.deadline || Infinity;
    return ad - bd;
  });
  return out;
}
